import { DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { SerializedInventoryMeterReadingGroup } from '@enmon/commons/lib/tools';
import { Col, Row, Space, Tooltip, Typography } from 'antd';
import { ColumnsType, FilterConfirmProps } from 'antd/lib/table/interface';
import { Button } from 'components/Button/Button';
import { Input } from 'components/Input/Input';
import React from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { ReadingGroupFilter } from 'services/api/inventoryMeterReadingGroups';
import { DeleteActionButton } from '../components/DeleteActionButton';
import { ReadingGroupsTableColumns as messages } from '../messages';

interface GetColumnsConfig {
  filter: ReadingGroupFilter;
  onSearch: (
    selectedKeys: React.Key[],
    confirm: (param?: FilterConfirmProps) => void,
    item: keyof ReadingGroupFilter,
  ) => void;
  onReset: (clearFilters: () => void, item: keyof ReadingGroupFilter) => void;
  intl: IntlShape;
  onDelete: (id: number) => void;
  isLoading?: boolean;
}

export const getColumns = ({
  filter,
  onSearch,
  intl,
  onDelete,
  isLoading,
}: GetColumnsConfig): ColumnsType<SerializedInventoryMeterReadingGroup> => {
  const { sortOrder, sortField } = filter;

  const getColumnSearchProps = (
    dataIndex: keyof ReadingGroupFilter,
  ): Pick<
    ColumnsType<SerializedInventoryMeterReadingGroup>[0],
    'filterDropdown' | 'filterIcon' | 'onFilter' | 'filteredValue'
  > => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, close }) => (
      <form
        onSubmit={e => {
          e.preventDefault();
          onSearch(selectedKeys, confirm, dataIndex);
        }}
      >
        <Space direction="vertical" style={{ padding: 8 }}>
          <Input
            allowClear
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={e => setSelectedKeys([e.target.value])}
          />
          <Row gutter={[8, 8]}>
            <Col span={12}>
              <Button block htmlType="submit" icon={<SearchOutlined />} size="small" type="primary">
                <FormattedMessage {...messages.search} />
              </Button>
            </Col>
            <Col span={12}>
              <Button block size="small" style={{ whiteSpace: 'pre-wrap' }} onClick={close}>
                <FormattedMessage {...messages.reset} />
              </Button>
            </Col>
          </Row>
        </Space>
      </form>
    ),
    filterIcon: () => (
      <div style={{ padding: '4px' }}>
        <SearchOutlined />
      </div>
    ),
    filteredValue: filter[dataIndex] != null ? [String(filter[dataIndex])] : null,
  });

  return [
    {
      width: 100,
      title: intl.formatMessage(messages.id),
      dataIndex: 'id' satisfies keyof SerializedInventoryMeterReadingGroup,
      ellipsis: true,
      fixed: 'left',
      key: 'id',
      sorter: true,
      sortOrder: sortField === 'id' ? sortOrder : undefined,
      render: id => <Link to={`/reading-groups/${id}`}>{id}</Link>,
      ...getColumnSearchProps('id'),
    },
    {
      width: 200,
      title: intl.formatMessage(messages.name),
      dataIndex: 'name' satisfies keyof SerializedInventoryMeterReadingGroup,
      ellipsis: true,
      key: 'name',
      sorter: true,
      sortOrder: sortField === 'name' ? sortOrder : undefined,
      onCell: () => ({
        style: {
          whiteSpace: 'nowrap',
          maxWidth: 200,
        },
      }),
      render: name => (
        <Tooltip title={name}>
          <Typography.Text>{name}</Typography.Text>
        </Tooltip>
      ),
      ...getColumnSearchProps('name'),
    },
    {
      width: 300,
      title: intl.formatMessage(messages.description),
      dataIndex: 'description' satisfies keyof SerializedInventoryMeterReadingGroup,
      ellipsis: true,
      key: 'description',
      onCell: () => ({
        style: {
          whiteSpace: 'nowrap',
          maxWidth: 300,
        },
      }),
      render: description => (
        <Tooltip title={description}>
          <Typography.Text>{description}</Typography.Text>
        </Tooltip>
      ),
    },
    {
      width: 100,
      title: intl.formatMessage(messages.meterCount),
      key: 'meterCount',
      render: (_, record) => record.inventory_meters?.length ?? 0,
    },
    {
      width: 80,
      key: 'actions',
      title: intl.formatMessage(messages.actions),
      render: (_, record) => (
        <DeleteActionButton
          confirmTitle={intl.formatMessage(messages.confirmDelete)}
          icon={<DeleteOutlined />}
          id={record.id}
          isLoading={isLoading}
          onDelete={onDelete}
        />
      ),
    },
  ];
};
